import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget (bot protection).
 *
 * Renders only when VITE_TURNSTILE_SITE_KEY is set — pair it with
 * TURNSTILE_SECRET on the API worker. With neither set (local dev),
 * the widget is hidden and the worker skips verification.
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''

export const turnstileEnabled = !!SITE_KEY

let scriptPromise = null
function loadScript() {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      s.async = true
      s.defer = true
      s.onload = resolve
      s.onerror = () => { scriptPromise = null; reject(new Error('Turnstile failed to load')) }
      document.head.appendChild(s)
    })
  }
  return scriptPromise
}

/**
 * @param onToken   (token|null) => void — called with a fresh token, or null on expiry/error
 * @param theme     'auto' | 'light' | 'dark'
 * @param invisible true (default) — widget runs silently with no visible UI.
 *                  false — renders the visible managed widget (shows a checkbox/spinner).
 *                  Match this to the widget type set in Cloudflare dashboard → Turnstile.
 */
export default function TurnstileWidget({ onToken, theme = 'dark', invisible = true }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  useEffect(() => {
    if (!SITE_KEY) return
    let cancelled = false

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme,
          size: invisible ? 'invisible' : 'flexible',
          callback: (token) => onTokenRef.current?.(token),
          'expired-callback': () => onTokenRef.current?.(null),
          'error-callback': () => onTokenRef.current?.(null),
        })
      })
      .catch(() => {})

    return () => {
      cancelled = true
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current) } catch { /* already gone */ }
        widgetIdRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!SITE_KEY) return null
  // Turnstile needs a real DOM node with layout to mount its iframe.
  // For invisible mode: position it off-screen so it never takes up space
  // but the browser still renders and executes it (display:none breaks the iframe).
  return (
    <div
      ref={containerRef}
      style={invisible
        ? { position: 'absolute', top: 0, left: 0, width: '1px', height: '1px', overflow: 'hidden', opacity: 0, pointerEvents: 'none' }
        : { minHeight: '65px' }
      }
    />
  )
}
